import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { uiActions, devicesActions } from '@/store';
import {
  Car, Map, LayoutDashboard, MapPin, MapPinned, Bell, FileText,
  Settings, UserCheck, FolderTree, Wrench, Plus, Users,
  Search, Route, CircleStop, BarChart3,
  Activity, Gauge, Sun, Moon, Monitor, Sparkles, Rows3, Rows4,
  ShieldCheck,
} from 'lucide-react';
import { selectGeofencesOnly, selectPOIs } from '@/store/selectors';
import useFeatures from '@/hooks/useFeatures';
import useTheme from '@/hooks/useTheme';

export default function CommandPalette() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.ui.commandPaletteOpen);

  const devices = useSelector((state) => state.devices.items);
  const drivers = useSelector((state) => state.drivers.items);
  const groups = useSelector((state) => state.groups.items);

  const features = useFeatures();
  const { setTheme } = useTheme();
  const viewDensity = useSelector((state) => state.ui.viewDensity);
  const [search, setSearch] = useState('');

  const close = useCallback(() => {
    dispatch(uiActions.setCommandPalette(false));
    setSearch('');
  }, [dispatch]);

  // Keyboard shortcut: Cmd+K / Ctrl+K + Escape to close
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        dispatch(uiActions.toggleCommandPalette());
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, open, close]);

  const runAction = useCallback((action) => {
    action();
    close();
  }, [close]);

  const pages = useMemo(() => [
    { label: t('nav.dashboard'), icon: LayoutDashboard, path: '/' },
    { label: t('nav.liveMap'), icon: Map, path: '/map' },
    { label: t('nav.alerts', 'Alerts'), icon: Bell, path: '/alerts' },
    { label: t('rules.rulesTab', 'Notification rules'), icon: ShieldCheck, path: '/alerts?tab=rules' },
    { label: t('nav.geofences'), icon: MapPin, path: '/geofences', feature: 'geofences' },
    { label: t('nav.pois'), icon: MapPinned, path: '/pois', feature: 'pois' },
    { label: t('reports.trips'), icon: Route, path: '/reports/trips', feature: 'reports' },
    { label: t('reports.stops'), icon: CircleStop, path: '/reports/stops', feature: 'reports' },
    { label: t('reports.summary'), icon: BarChart3, path: '/reports/summary', feature: 'reports' },
    { label: t('nav.settings'), icon: Settings, path: '/settings' },
    { label: t('nav.vehicles', 'Vehicles'), icon: Car, path: '/vehicles' },
    { label: t('org.members', 'Members'), icon: Users, path: '/settings/members' },
    { label: t('settings.drivers'), icon: UserCheck, path: '/settings/drivers/list' },
    { label: t('settings.groups'), icon: FolderTree, path: '/settings/groups' },
    { label: t('settings.maintenance'), icon: Wrench, path: '/settings/maintenance', feature: 'maintenance' },
    { label: t('settings.connectivity', 'Connectivity'), icon: Activity, path: '/settings/connectivity' },
    { label: t('settings.sensorHealth', 'Sensor Health'), icon: Gauge, path: '/settings/sensors/health' },
  ].filter((p) => !p.feature || features[p.feature]), [t, features]);

  const toggleLiveMapAnimate = useCallback(() => {
    try {
      const cur = localStorage.getItem('trackizy_livemap_animate_positions');
      const next = cur === 'false' ? 'true' : 'false';
      localStorage.setItem('trackizy_livemap_animate_positions', next);
      window.dispatchEvent(new CustomEvent('trackizy-livemap-animate-change', { detail: { enabled: next === 'true' } }));
    } catch { /* private mode */ }
  }, []);

  const quickActions = useMemo(() => [
    { label: t('commandPalette.addDevice'), icon: Plus, action: () => navigate('/vehicles') },
    { label: t('commandPalette.addDriver'), icon: Plus, action: () => navigate('/settings/drivers/list') },
    { label: t('commandPalette.createGeofence'), icon: Plus, action: () => navigate('/geofences'), feature: 'geofences' },
    { label: t('commandPalette.addGroup'), icon: Plus, action: () => navigate('/settings/groups') },
    { label: t('commandPalette.addMaintenance'), icon: Plus, action: () => navigate('/settings/maintenance'), feature: 'maintenance' },
    { label: t('commandPalette.createRule', 'Create notification rule'), icon: Plus, action: () => navigate('/alerts?tab=rules&new=1') },
    { label: t('commandPalette.runTripReport'), icon: Route, action: () => navigate('/reports/trips'), feature: 'reports' },
    { label: t('commandPalette.runStopReport'), icon: CircleStop, action: () => navigate('/reports/stops'), feature: 'reports' },
    { label: t('commandPalette.runSummaryReport'), icon: BarChart3, action: () => navigate('/reports/summary'), feature: 'reports' },
    { label: t('commandPalette.themeLight', 'Theme: Light'), icon: Sun, action: () => setTheme('light') },
    { label: t('commandPalette.themeDark', 'Theme: Dark'), icon: Moon, action: () => setTheme('dark') },
    { label: t('commandPalette.themeSystem', 'Theme: System'), icon: Monitor, action: () => setTheme('system') },
    {
      label: viewDensity === 'compact'
        ? t('commandPalette.densityComfortable', 'Switch to comfortable list density')
        : t('commandPalette.densityCompact', 'Switch to compact list density'),
      icon: viewDensity === 'compact' ? Rows3 : Rows4,
      action: () => dispatch(uiActions.setViewDensity(viewDensity === 'compact' ? 'comfortable' : 'compact')),
    },
    {
      label: t('commandPalette.toggleAnimation', 'Toggle live-map marker animation'),
      icon: Sparkles,
      action: toggleLiveMapAnimate,
    },
  ].filter((a) => !a.feature || features[a.feature]), [t, navigate, features, dispatch, setTheme, viewDensity, toggleLiveMapAnimate]);

  const deviceList = useMemo(() => Object.values(devices), [devices]);
  const geofenceList = useSelector(selectGeofencesOnly);
  const poiList = useSelector(selectPOIs);
  const driverList = useMemo(() => Object.values(drivers), [drivers]);
  const groupList = useMemo(() => Object.values(groups), [groups]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={close}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative flex justify-center pt-[15vh]" onClick={(e) => e.stopPropagation()}>
        <Command
          className="w-full max-w-[560px] rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1)',
          }}
          label="Command palette"
        >
          <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Search size={16} className="text-slate-400 flex-shrink-0" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={t('commandPalette.placeholder')}
              className="flex-1 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 outline-none bg-transparent"
              autoFocus
            />
            <kbd className="text-[10px] text-slate-400 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">ESC</kbd>
          </div>

          <Command.List className="max-h-[360px] overflow-y-auto p-2">
            <Command.Empty className="py-8 text-center text-sm text-slate-400">
              {t('commandPalette.noResults')}
            </Command.Empty>

            {deviceList.length > 0 && (
              <Command.Group heading={t('commandPalette.devices')} className="mb-1">
                {deviceList.slice(0, search ? undefined : 5).map((d) => (
                  <Command.Item
                    key={`device-${d.id}`}
                    value={`${d.name} ${d.uniqueId} ${d.phone || ''}`}
                    onSelect={() => runAction(() => {
                      dispatch(devicesActions.selectId(d.id));
                      navigate('/map');
                    })}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                  >
                    <Car size={16} className="text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{d.name}</span>
                    <span className="text-[10px] text-slate-400 tabular-nums">{d.uniqueId}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {features.geofences && geofenceList.length > 0 && (
              <Command.Group heading={t('commandPalette.geofences')} className="mb-1">
                {geofenceList.map((g) => (
                  <Command.Item
                    key={`geo-${g.id}`}
                    value={g.name}
                    onSelect={() => runAction(() => navigate('/geofences'))}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                  >
                    <MapPin size={16} className="text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{g.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {features.pois && poiList.length > 0 && (
              <Command.Group heading={t('commandPalette.pois')} className="mb-1">
                {poiList.map((p) => (
                  <Command.Item
                    key={`poi-${p.id}`}
                    value={p.name}
                    onSelect={() => runAction(() => navigate(`/pois?select=${p.id}`))}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                  >
                    <MapPinned size={16} className="text-blue-500 flex-shrink-0" />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-xs text-slate-400">{p.poi_categories?.name || ''}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {driverList.length > 0 && (
              <Command.Group heading={t('commandPalette.drivers')} className="mb-1">
                {driverList.map((d) => (
                  <Command.Item
                    key={`driver-${d.id}`}
                    value={`${d.name} ${d.uniqueId}`}
                    onSelect={() => runAction(() => navigate('/settings/drivers/list'))}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                  >
                    <UserCheck size={16} className="text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{d.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {groupList.length > 0 && (
              <Command.Group heading={t('commandPalette.groups')} className="mb-1">
                {groupList.map((g) => (
                  <Command.Item
                    key={`group-${g.id}`}
                    value={g.name}
                    onSelect={() => runAction(() => navigate('/settings/groups'))}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                  >
                    <FolderTree size={16} className="text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{g.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group heading={t('commandPalette.pages')} className="mb-1">
              {pages.map((p) => (
                <Command.Item
                  key={`page-${p.path}`}
                  value={p.label}
                  onSelect={() => runAction(() => navigate(p.path))}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                >
                  <p.icon size={16} className="text-slate-400 flex-shrink-0" />
                  <span className="flex-1 truncate">{p.label}</span>
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Group heading={t('commandPalette.quickActions')} className="mb-1">
              {quickActions.map((a, i) => (
                <Command.Item
                  key={`action-${i}`}
                  value={a.label}
                  onSelect={() => runAction(a.action)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-500/10 data-[selected=true]:text-blue-700 dark:data-[selected=true]:text-blue-400 text-slate-700 dark:text-slate-300"
                >
                  <a.icon size={16} className="text-blue-500 flex-shrink-0" />
                  <span className="flex-1 truncate">{a.label}</span>
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
